(() => {
  const celebrated = new Set();
  const progressByContainer = new WeakMap();

  // Shared with the free-shipping goal: same particle preset and animation.
  window.celebrateCartGoal = async function(key, goal) {
    const drawer = goal?.closest('#cart-notification');
    const container = drawer
      ? (drawer.classList.contains('active') ? drawer : null)
      : goal?.closest('.main-cart') || document.querySelector('#cart-notification.active, .main-cart');
    if (!container || !window.tsParticles || typeof window.loadConfettiPreset !== 'function') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const storageKey = 'cart-goal-celebrated:' + key;
    if (celebrated.has(storageKey)) return;
    try { if (sessionStorage.getItem(storageKey)) return; } catch (_) { /* Use in-memory state. */ }
    celebrated.add(storageKey);
    try { sessionStorage.setItem(storageKey, 'yes'); } catch (_) { /* Use in-memory state. */ }
    // Only one particle canvas is needed when two offers qualify together.
    if (document.getElementById('tsparticles')) return;
    const canvas = document.createElement('div');
    canvas.id = 'tsparticles';
    canvas.setAttribute('aria-hidden', 'true');
    container.prepend(canvas);
    let particles;
    try {
      await window.loadConfettiPreset(window.tsParticles);
      if (!canvas.isConnected) return;
      particles = await window.tsParticles.load('tsparticles', {
        fullScreen: { enable: false },
        particles: {
          color: { value: ['#EF2964', '#00C09D', '#2D87B0', '#48485E', '#EFFF1D'] },
          shape: { type: ['circle', 'square', 'triangle'] },
          move: {
            direction: 'top', enable: true,
            outModes: { top: 'none', default: 'destroy' },
            gravity: { enable: true }, speed: 80
          },
          number: { value: 0, limit: 300 },
          size: {
            value: { min: 4, max: 6 },
            animation: { count: 5, startValue: 'min', enable: true, speed: 50, sync: true }
          },
          tilt: {
            direction: 'random', enable: true, value: { min: 0, max: 450 },
            animation: { enable: true, speed: 300 }
          }
        },
        emitters: [{
          life: { duration: 3, count: 1 }, position: { x: 50, y: 0 },
          particles: { move: { direction: 'top-bottom' } },
          rate: { quantity: 10, delay: 0.1 }, size: { width: 0, height: 0 }
        }],
        preset: 'confetti'
      });
    } catch (_) {
      // A failed decorative animation must not affect the cart.
    } finally {
      window.setTimeout(() => { particles?.destroy(); canvas.remove(); }, 7000);
    }
  };

  class CartGiftGoal extends HTMLElement {
    connectedCallback() {
      this.goalContainer = this.closest('#cart-notification') || this.closest('.main-cart') || this.parentElement;
      this.progressKey = [this.dataset.collection, this.dataset.threshold, this.dataset.currency].join(':');
      if (!progressByContainer.has(this.goalContainer)) progressByContainer.set(this.goalContainer, new Map());
      this.refresh();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.progressFrame);
      const replacement = Array.from(this.goalContainer.querySelectorAll('cart-gift-goal')).some((goal) =>
        goal.isConnected && [goal.dataset.collection, goal.dataset.threshold, goal.dataset.currency].join(':') === this.progressKey
      );
      if (!replacement) progressByContainer.get(this.goalContainer).delete(this.progressKey);
    }

    refresh() {
      const rate = Number(window.Shopify?.currency?.rate || 1);
      const threshold = Math.round(Number(this.dataset.threshold) * rate * 100);
      if (!Number.isFinite(threshold) || threshold <= 0) return;
      const subtotal = Number(this.dataset.subtotal);
      const qualified = subtotal >= threshold;
      const claimed = this.dataset.giftApplied === 'true';
      const progress = Math.min(100, Math.max(0, Math.floor(subtotal * 100 / threshold)));
      const remaining = Math.max(0, threshold - subtotal);
      const money = typeof window.Shopify?.formatMoney === 'function'
        ? window.Shopify.formatMoney(remaining)
        : new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
          style: 'currency', currency: this.dataset.currency || 'CAD'
        }).format(remaining / 100);
      const messageType = claimed ? 'claimed' : qualified ? 'ready' : 'before';
      const message = this.querySelector('[data-gift-' + messageType + ']').content.textContent;
      // Merchant text stays text; formatted amounts may contain money-format markup.
      const amountText = document.createElement('span');
      amountText.innerHTML = money;
      const messageNode = this.querySelector('[data-gift-message]');
      const messageParts = message.replace(/\{\{amount\}\}/g, amountText.textContent).split(/(\*\*[^*]+\*\*)/g);
      messageNode.replaceChildren(...messageParts.map((part) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          const strong = document.createElement('strong');
          strong.textContent = part.slice(2, -2);
          return strong;
        }
        return document.createTextNode(part);
      }));
      this.querySelector('.gift-goal-link').hidden = !qualified && !claimed;
      this.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', String(progress));
      this.querySelector('.goal-badge').textContent = String(progress);
      const bar = this.querySelector('.cart-goal__bar > span');
      const previousProgress = progressByContainer.get(this.goalContainer);
      if (!this.progressInitialized) {
        // Cart section rendering replaces this element. Restore its last position
        // before allowing the shared width transition to animate the new value.
        bar.style.setProperty('transition', 'none');
        bar.style.setProperty('--progress', (previousProgress.get(this.progressKey) ?? progress) + '%');
        void bar.offsetWidth;
        bar.style.removeProperty('transition');
        this.progressInitialized = true;
      }
      previousProgress.set(this.progressKey, progress);
      cancelAnimationFrame(this.progressFrame);
      this.progressFrame = requestAnimationFrame(() => {
        if (this.isConnected) bar.style.setProperty('--progress', progress + '%');
      });
      if (qualified || claimed) {
        window.celebrateCartGoal('gift:' + this.dataset.collection + ':' + threshold, this);
      }
    }
  }

  customElements.define('cart-gift-goal', CartGiftGoal);
})();
